"use client";

/**
 * Generic sort picker - THE sort control for the whole app. Replaces
 * native <select>s (whose OS wheel picker + plain box read poorly on
 * mobile and clash with the app's custom filter chips). Same dual
 * behaviour as the filter Chip:
 *   - desktop (sm+): an absolute dropdown of options
 *   - mobile (<sm):  a slide-up bottom sheet above the nav bar (z-[1200])
 *
 * Direction is baked into the options (e.g. "price" / "price_desc")
 * instead of a separate asc/desc toggle, so every surface sorts the
 * same way. Consumers pass their own option list; the listings page
 * wraps this in ListingsSortPicker.
 *
 * Dark mode: bare light-mode classes only (the .dark var-swap flips
 * them); the active option is a grey row with a check (ROW_ON, the
 * quiet UI).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ROW_ON } from "@/lib/ui-classes";
import { createPortal } from "react-dom";
import { prefersReducedMotion } from "@/lib/motion";
import { ArrowUpDown, ChevronDown, Check } from "lucide-react";
import { useT } from "@/lib/locale-context";
import { useSheetDrag } from "@/lib/use-sheet-drag";
import { useBackToClose } from "@/lib/use-back-to-close";
import SheetGrip from "@/components/SheetGrip";
import { haptic } from "@/lib/haptics";
import type { TranslationKey } from "@/lib/i18n";

const SHEET_MS = 200;

export interface SortOption {
  value: string;
  key: TranslationKey;
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SortOption[];
  /** Trigger aria-label + mobile sheet header. */
  labelKey?: TranslationKey;
  /** Show the current option's label inside the trigger at sm+. */
  showValueLabel?: boolean;
}

export default function SortPicker({
  value,
  onChange,
  options,
  labelKey = "listings.sort.label",
  showValueLabel = true,
}: Props) {
  const t = useT();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = options.find((s) => s.value === value) ?? options[0];

  const close = useCallback(() => {
    if (isMobile) {
      setEntered(false);
      setTimeout(() => setOpen(false), SHEET_MS);
    } else {
      setOpen(false);
    }
  }, [isMobile]);

  // Back (the phone's, or Telegram's in the Mini App) closes the sheet.
  useBackToClose(open && isMobile, close);

  // Desktop: outside-click closes.
  useEffect(() => {
    if (!open || isMobile) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, isMobile]);

  // Mobile: enter animation + body scroll lock.
  useEffect(() => {
    if (!open || !isMobile) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") close(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  // Mobile: drag the sheet header down to dismiss.
  const { offset: dragOffset, dragging, handleProps, panelRef } = useSheetDrag(close, open);

  function pick(v: string) {
    haptic(6);
    onChange(v);
    close();
  }

  const optionList = (
    <div role="listbox" className="flex flex-col">
      {options.map(({ value: v, key }) => {
        const active = v === value;
        return (
          <button
            key={v}
            type="button"
            role="option"
            aria-selected={active}
            onClick={() => pick(v)}
            className={`flex items-center justify-between gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
              active ? ROW_ON : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span>{t(key)}</span>
            {active && <Check className="w-4 h-4 shrink-0" />}
          </button>
        );
      })}
    </div>
  );

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t(labelKey)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors"
      >
        <ArrowUpDown className="w-4 h-4 shrink-0" />
        {showValueLabel && <span className="hidden sm:inline">{t(current.key)}</span>}
        <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open && !isMobile ? "rotate-180" : ""}`} />
      </button>

      {/* Desktop dropdown */}
      {open && !isMobile && (
        <div className="absolute right-0 z-30 mt-2 w-52 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl">
          {optionList}
        </div>
      )}

      {/* Mobile bottom sheet - a REAL portal to document.body: `fixed`
          alone breaks inside ancestors with backdrop-filter/transform
          (the /map frosted island), which re-anchored the sheet and
          buried it under the tab bar. */}
      {open && isMobile && typeof document !== "undefined" && createPortal(
        <>
          <div
            onClick={close}
            aria-hidden="true"
            style={{ opacity: entered ? 1 : 0, transition: `opacity ${SHEET_MS}ms ease-out` }}
            className="fixed inset-0 z-[1190] bg-black/40"
          />
          <div ref={panelRef}
            style={{
              // Reduce Motion: a cross-fade in place, as in BottomSheet.
              transform: entered ? `translateY(${dragOffset}px)` : prefersReducedMotion() ? "none" : "translateY(100%)",
              opacity: entered || !prefersReducedMotion() ? 1 : 0,
              transition: dragging ? "none" : `transform ${SHEET_MS}ms ease-out, opacity ${SHEET_MS}ms ease-out`,
            }}
            className="fixed inset-x-0 bottom-0 z-[1200] rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl"
          >
            {/* Header - drag down to dismiss. */}
            <div {...handleProps} className="flex items-center justify-between mb-3 touch-none">
              <SheetGrip />
              <span className="text-sm font-semibold text-gray-900">{t(labelKey)}</span>
              {/* No visible X on a phone sheet (owner, 2026-10-23): a swipe down, a tap outside, Back or Escape close it; screen readers keep the button. */}
              <button type="button" onClick={close} className="sr-only">
                {t("listings.detail.close")}
              </button>
            </div>
            {optionList}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
